import { PayPalButtons, PayPalScriptProvider } from '@paypal/react-paypal-js';
import React from 'react';
import { useFormContext } from 'react-hook-form';
import { t } from 'i18next';
import useToast from 'apps/commerce/common/checkout/hooks/useToast';
import { getSiteLocale, postRequest } from 'apps/commerce/popup_checkout/util';

interface PayPalButtonProps {
  onSubmit: () => void;
  disable: boolean;
}

const PayPalButton = ({ onSubmit, disable }: PayPalButtonProps) => {
  const { setValue, getValues } = useFormContext();
  const { showToast } = useToast() as {
    showToast: (params: { title: string; type: string }) => void;
  };
  const { checkoutSettings } = getValues();
  const { paypalClientId: clientId, paypalMerchantId: merchantId } = checkoutSettings;

  const price = {
    currency: 'USD',
  };

  const handlePaypalRequestError = (err: string) => {
    if (err && err.includes('CURRENCY')) {
      showToast({ title: t('messages.paypal_invalid_currency'), type: 'danger' });
    } else {
      showToast({ title: t('messages.paypal_default_error'), type: 'danger' });
    }
  };

  const buildPayPalRequestOptions = () => {
    const { coupon, addressRequired, address, collectTaxId, taxId } = getValues();
    const addressInfoRequired = addressRequired || collectTaxId;

    return {
      ...(coupon && { couponCode: coupon.couponCode }),
      ...(addressInfoRequired && {
        addressInfo: {
          ...(collectTaxId && { businessNumber: taxId }),
          address_line_1: address.addressLine1,
          address_line_2: address.addressLine2,
          addressCity: address.city,
          addressZip: address.zip,
          addressCountry: address.country,
          ...(address.subdivisionRequired && { addressState: address.subdivision }),
        },
      }),
    };
  };

  const createPayPalOrder = async () => {
    const options: Record<string, any> = {
      returnOrderId: true,
      ...buildPayPalRequestOptions(),
    };
    // TODO: update this to use new Order Paypal Payments API
    const resp = await postRequest(`/offers/${'sdfsdfsdf'}/paypal_payments`, options)
      // Catch any errors when initializing the PayPal order
      .catch((err: any) => {
        const errorMessage = err.response.data.error;
        handlePaypalRequestError(errorMessage);
      });
    const orderId = resp?.data?.token;
    if (orderId) {
      setValue('paypalOrderId', orderId);
      return orderId;
    }
    return null;
  };

  // @ts-ignore
  const scriptOptions: any = {
    clientId,
    currency: price.currency.toUpperCase(),
    locale: getSiteLocale(),
  };

  if (merchantId) {
    // @ts-ignore
    scriptOptions.merchantId = merchantId;
  }

  return (
    <span data-testid="paypal-button" className="sage-btn--full-width">
      {/* @ts-ignore */}
      <PayPalScriptProvider options={scriptOptions}>
        <PayPalButtons
          fundingSource="paypal" // don't show debit/credit card button
          style={{
            label: 'pay', // this is the PayPal button text option 'Pay with PayPal'
            tagline: false, // don't show "Powered by PayPal"
            color: 'blue',
            height: 37, // match button height with Kajabi theme
          }}
          createOrder={createPayPalOrder}
          createBillingAgreement={undefined}
          createSubscription={undefined}
          // @ts-ignore
          onApprove={async (data: any) => {
            setValue('paypalPayerId', data.payerID);
            onSubmit();
            return Promise.resolve();
          }}
          onCancel={() => {
            setValue('paypalOrderId', null);
            setValue('paypalPayerId', null);
          }}
          onError={(e: any) => {
            // we are handling the error in createPayPalOrder so just throw it here on error
            // to prevent the default error message from showing up in the console
            setValue('paypalOrderId', null);
            setValue('paypalPayerId', null);
            throw new Error(typeof e === 'string' ? e : 'PayPal Error');
          }}
          disabled={disable}
        />
      </PayPalScriptProvider>
    </span>
  );
};

export default PayPalButton;
